/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Section, Container, Box, Link, Text, Flex } from '@tishman/components';
import { useStaticQuery, graphql } from 'gatsby';
import React, { useMemo } from 'react';
import { H } from '@hzdg/sectioning';
import invariant from 'invariant';

import AtlasLargeFeature from '../components/AtlasLargeFeature';
import AtlasMiniFeature from '../components/AtlasMiniFeature';
import {
  mergeWithFeaturedStories,
  isTruthy,
  formatStoryCategory,
} from '../utils';

import type { ComponentPropsWithoutRef } from 'react';

const HOLIDAYS_FEATURED_STORIES_QUERY = graphql`
  query HolidaysFeaturedStories {
    sanityAttractionHoliday(
      _id: { eq: "theHolidays" }
      _type: { eq: "attraction.holiday" }
    ) {
      featuredStories {
        ...storyFragment
      }
    }
    latestStoryNodes: allSanityStory(sort: { publishAt: DESC }, limit: 4) {
      edges {
        node {
          ...storyFragment
        }
      }
    }
    dataJson(jsonId: { eq: "holidays" }) {
      featuredStories {
        title
        caption
        link {
          label
          url
        }
      }
    }
  }
`;

const HolidaysFeaturedStoriesBlock = (
  props: ComponentPropsWithoutRef<typeof Section>,
): React.JSX.Element => {
  const { sanityAttractionHoliday, latestStoryNodes, dataJson } =
    useStaticQuery<Queries.HolidaysFeaturedStoriesQuery>(
      HOLIDAYS_FEATURED_STORIES_QUERY,
    );
  const latestStories = latestStoryNodes.edges.flatMap((v) => v.node);

  invariant(dataJson, 'Holidays JSON data is required!');

  const holidaysFeaturedStoriesProps = useMemo(() => {
    const featuredStories = (
      sanityAttractionHoliday?.featuredStories || []
    ).filter(isTruthy);

    return {
      title: dataJson.featuredStories.title,
      caption: dataJson.featuredStories.caption,
      link: dataJson.featuredStories.link,
      featuredStories: mergeWithFeaturedStories({
        otherStories: latestStories,
        featuredStories,
      }).map((story) => {
        return {
          title: story?.titleAndSlug?.title,
          excerpt: story?.excerpt,
          url: `/magazine/${formatStoryCategory(story?.category ?? '')}/${
            story?.titleAndSlug?.slug?.current
          }`,
          alt: story?.poster?.alt,
          image: story?.poster?.asset?.gatsbyImageData,
          category: story?.category,
          formattedPublishAt: story.formattedPublishAt,
        };
      }),
    };
  }, [sanityAttractionHoliday, latestStories, dataJson]);

  return (
    holidaysFeaturedStoriesProps && (
      <Section data-section="HolidaysFeaturedStories" {...props} sx={{ py: 8 }}>
        <Container sx={{ px: 0 }}>
          <Box sx={{ textAlign: 'center', py: 0 }}>
            <H
              sx={{
                variant: 'styles.h1',
                mb: 3,
                fontFamily: 'headingSecondary',
                color: 'heroBackground',
              }}
            >
              {holidaysFeaturedStoriesProps.title}
            </H>
            <Text
              as="p"
              sx={{
                variant: 'text.mediumP',
                mb: 3,
                color: 'heroBackground',
                maxWidth: 730,
                margin: '0 auto',
              }}
            >
              {holidaysFeaturedStoriesProps.caption}
            </Text>
          </Box>
          <Container
            sx={{
              pb: 8,
              px: 0,
              py: [0, null, null, null, 5],
              maxWidth: 1200,
              display: 'flex',
              flexDirection: 'column',
              columnGap: '56px',
            }}
          >
            <AtlasLargeFeature
              data={holidaysFeaturedStoriesProps.featuredStories[0]}
            />
            <Flex
              sx={{
                display: 'flex',
                flexDirection: ['column', null, null, null, 'row'],
                padding: ['0 40px', null, 'initial'],
                width: '100%',
              }}
            >
              {holidaysFeaturedStoriesProps.featuredStories
                .slice(1)
                .map((item) => (
                  <AtlasMiniFeature data={item} key={item.title} />
                ))}
            </Flex>
          </Container>
          <Box
            sx={{
              textAlign: 'center',
              width: 'fit-content',
              my: 3,
              mx: 'auto',
            }}
          >
            <Link
              href={holidaysFeaturedStoriesProps.link.url}
              sx={{
                'color': 'heroBackground',
                '::after': { backgroundColor: 'heroBackground' },
                'border': '1px solid #FFFFFF',
                '&:hover': {
                  backgroundColor: 'CREAM',
                  border: '1px solid #F8E9DB',
                  color: 'BLACK',
                },
              }}
              variant="buttonBorder"
            >
              {holidaysFeaturedStoriesProps.link.label}
            </Link>
          </Box>
        </Container>
      </Section>
    )
  );
};

export default HolidaysFeaturedStoriesBlock;
